import { Box, Button, Modal } from '@warp-ds/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import { ImageGallery } from 'shared/components/imageGallery/ImageGallery';
import { useConfig } from 'shared/config/ConfigContext';
import { useBreakpoints } from 'shared/hooks/useBreakpoints';

import { Breadcrumbs } from '@/components/breadcrumbs/Breadcrumbs';
import { HotelEditPanel } from '@/components/hotel/HotelEditPanel';
import { useHotelResults } from '@/hooks/useHotelResults';
import { useInitialImageIndex } from '@/hooks/useInitialImageIndex';

import { ApiActionStatus, type HotelOffer, type PaleState, PRICE_MIGHT_VARY_WARNING, type ThunkDispatchFunc } from '@/interfaces';

import { formatDistances, getAmenitiesText, getTopAmenities, groupAmenitiesByCategory, parseMarkdownSections } from '@/lib/utils/hotelData';
import { trackHotelPage, trackOpenAboutModal, trackOpenFacilitiesModal } from '@/lib/utils/tracking';
import { actions } from '@/state/filterActions';
import { fetchHotelResults } from '@/state/hotelResultActions';
import { EmptyWarning } from '../emptyWarning/EmptyWarning';
import { StarRating } from '../resultList/components/StarRating';
import { HotelResultItem } from './HotelResultItem';
import { NoHotelWarning } from './NoHotelWarning';

export const HotelPage = () => {
    const dispatch = useDispatch<ThunkDispatchFunc>();
    const { hotel, filters } = useSelector((state: PaleState) => ({ hotel: state.hotel, filters: state.filters }), shallowEqual);

    const [showAmenitiesModal, setShowAmenitiesModal] = useState(false);
    const [showAboutModal, setShowAboutModal] = useState(false);
    const { offers, status, offersLeft, fetchMoreOffers } = useHotelResults();
    const { imgPath } = useConfig();
    const isMobile = useBreakpoints({ breakpoint: 'md' });
    const initialImage = useInitialImageIndex();

    const allAmenities = hotel?.amenities;
    const topAmenities = useMemo(() => getTopAmenities(allAmenities ?? []), [allAmenities]);
    const groupedAmenities = useMemo(() => groupAmenitiesByCategory(allAmenities ?? []), [allAmenities]);

    const handleReset = useCallback(() => {
        dispatch(actions.resetHotelFilters());
        dispatch(fetchHotelResults());
    }, [dispatch]);

    useEffect(() => {
        if (hotel) {
            trackHotelPage(hotel);
        }
    }, [hotel]);

    if (!hotel?.id) {
        return <NoHotelWarning filters={filters} />;
    }

    const parsedHotelSummaryLong = hotel.summaryLong ? parseMarkdownSections(hotel.summaryLong) : null;
    const facilitiesText = getAmenitiesText(hotel);
    const distancesText = formatDistances(hotel.distanceToBeach, hotel.distanceToCity);
    const destinationCollection = [hotel.destination, hotel.region, hotel.country].filter(Boolean).join(' / ');
    const images = (hotel.imagePaths ?? []).map((p) => ({ uri: `${imgPath}1280w${p}`, width: 1280, height: 720 }));
    const shouldShowPriceWarning = filters.agesOfChildren?.some((age: number) => age > 12 || age < 2);

    return (
        <>
            <div className="flex flex-col gap-24 mb-32 pt-16">
                <Breadcrumbs data-testid="topBar" vertical="pp" hotelName={hotel.name} filters={filters} />

                <ImageGallery images={images} initialImage={initialImage} roundAllCorners showDescription={false} isMobile={isMobile} />

                <section className="pb-16" data-testid="hotelDescription">
                    <p className="s-text-subtle">{destinationCollection}</p>
                    <h1 className="py-8">{hotel.name}</h1>
                    <StarRating id="hotelRating" rating={hotel.rating} emptyStars />

                    {distancesText && <p className="font-bold pt-12">{distancesText}</p>}

                    {!hotel.amenities && facilitiesText && <p className="s-text-subtle">{facilitiesText}</p>}
                </section>

                <HotelEditPanel />

                {topAmenities.length > 0 && (
                    <section className="flex flex-col gap-16 items-start">
                        <h2 className="t3">Fasiliteter</h2>
                        <ul className="grid grid-cols-2 md:grid-cols-4 gap-16 list-none p-0 m-0 w-full">
                            {topAmenities.map(({ name, icon }) => (
                                <li key={name} className="flex items-center gap-8">
                                    <w-icon name={icon} size="small" locale="nb" aria-hidden></w-icon>
                                    <span className="text-body">{name}</span>
                                </li>
                            ))}
                        </ul>
                        <button
                            type="button"
                            className="mt-16 mb-8 underline text-body bg-transparent border-none cursor-pointer p-0"
                            aria-haspopup="dialog"
                            aria-label="Vis alle fasiliteter"
                            onClick={() => {
                                trackOpenFacilitiesModal(hotel.id, hotel.name);
                                setShowAmenitiesModal(true);
                            }}>
                            Vis alle
                        </button>
                        <hr className="w-full" aria-hidden />
                    </section>
                )}

                {hotel.summaryShort && (
                    <section className="flex flex-col gap-16 items-start">
                        <h2 className="t3">Om hotellet</h2>
                        <p className="text-body">{hotel.summaryShort}</p>

                        {parsedHotelSummaryLong && (
                            <button
                                type="button"
                                className="mt-16 mb-8 underline text-body bg-transparent border-none cursor-pointer p-0"
                                aria-haspopup="dialog"
                                aria-label="Vis mer om hotellet"
                                onClick={() => {
                                    trackOpenAboutModal(hotel.id, hotel.name);
                                    setShowAboutModal(true);
                                }}>
                                Vis mer
                            </button>
                        )}
                        <hr className="w-full" aria-hidden />
                    </section>
                )}

                {shouldShowPriceWarning && (
                    <Box info>
                        <div className="flex gap-x-16">
                            <w-icon name="Info" size="medium" locale="nb" className="s-text-link mt-2" aria-hidden></w-icon>
                            <p className="mb-0">{PRICE_MIGHT_VARY_WARNING}</p>
                        </div>
                    </Box>
                )}

                {offers.length <= 0 ? (
                    <EmptyWarning onReset={handleReset} testId="emptyWarning-hotel" />
                ) : (
                    offers.map((offer: HotelOffer, index) => (
                        <HotelResultItem
                            key={offer.offerId}
                            offer={offer}
                            hotel={hotel}
                            index={index}
                            filterNumberOfAdults={filters.numberOfAdults}
                            filterAgesOfChildren={filters.agesOfChildren}
                        />
                    ))
                )}

                {offersLeft > 0 && (
                    <Button link className="font-bold flex mx-auto" onClick={fetchMoreOffers} disabled={status === ApiActionStatus.PENDING}>
                        Last inn flere tilbud
                    </Button>
                )}
            </div>
            <Modal open={showAmenitiesModal} title="Fasiliteter" right onDismiss={() => setShowAmenitiesModal(false)}>
                <div className="flex flex-col gap-24">
                    {groupedAmenities.map(({ category, amenities: items }) => (
                        <section key={category}>
                            <h2 className="t4 mb-12">{category}</h2>
                            <ul className="grid grid-cols-2 gap-16 list-none p-0 m-0">
                                {items.map(({ name, icon }) => (
                                    <li key={name} className="flex items-center gap-8">
                                        <w-icon name={icon} size="small" locale="nb" aria-hidden></w-icon>
                                        <span className="text-body">{name}</span>
                                    </li>
                                ))}
                            </ul>
                        </section>
                    ))}
                </div>
            </Modal>
            <Modal open={showAboutModal} title="Om hotellet" right onDismiss={() => setShowAboutModal(false)}>
                <div className="flex flex-col gap-16">
                    {parsedHotelSummaryLong?.map(({ title, body }) => (
                        <section key={title}>
                            <h2 className="t4 mb-8">{title}</h2>
                            <p className="text-body">{body}</p>
                        </section>
                    ))}
                    <p className="text-caption font-bold italic flex items-center">
                        <w-icon name="Sparkles" size="small" locale="nb" className="mr-4" aria-hidden></w-icon>Beskrivelsen er oppsummert
                        ved hjelp av AI. Innholdet er hentet fra våre reisepartnere.
                    </p>
                </div>
            </Modal>
        </>
    );
};
